import { BehaviorSubject, Subject, debounceTime } from 'rxjs';
import { Comment } from './comment';
import { CoordinatorService, MANIFEST_STORE_DELAY } from './coordinator.service';
import { Attachment } from './attachment';

export class Comment$ {
    private _comMap = new Map<string, Map<string, Comment>>();

    activeComment$ = new BehaviorSubject<Comment | undefined>(undefined);

    private _markForManifestStoreSubject$ = new Subject<void>();
    private _markForManifestStore$ = this._markForManifestStoreSubject$.pipe(
        debounceTime(MANIFEST_STORE_DELAY),
    );

    constructor(private coords: CoordinatorService) {
        this._markForManifestStore$.subscribe(() => this._manifestStore());
    }

    clear() {
        this._comMap.clear();
        this.activeComment$.next(undefined);
    }

    clearCurrentUser() {
        this._comMap.forEach((mp) => {
            Array.from(mp.values()).forEach((c) => {
                if (c.autouserId) return;
                if (!c.ownerId || c.ownerId == this.coords.user$.current?.id) mp.delete(c.id);
            });
        });
        this.markForManifestStore();
    }

    markForManifestStore() {
        this._markForManifestStoreSubject$.next();
    }
    private _manifestStore() {
        this.coords.manifest$.store({
            type: 'comment',
            id: `commentLs_${this.coords.project$.currentId}`,
            valueMap: new Map<string, any[]>([['commentLs', this.storeLs()]]),
        });
    }

    storeLs() {
        const u = this.coords.user$.current;
        const ls: any[] = [];
        this._comMap.forEach((mp) =>
            mp.forEach((c) => (!u || u.id === c.ownerId) && c.text && ls.push(c.save())),
        );
        return ls;
    }

    resetProjectMember(memberId: string) {
        this._comMap.forEach((mp) => {
            Array.from(mp.values()).forEach((c) => {
                if (c.ownerId === memberId) mp.delete(c.id);
            });
        });
    }

    exportJsonAllXitem(xitemId: string) {
        return this.coords.comment$
            .ofOriginLs(xitemId)
            .map((c) => ({ user: c.ownerId, created: c.date.toString(), text: c.text }))
            .filter((x) => !!x.text);
    }

    load3(params: any) {
        if (!params) return;
        const otherLs: any[] = [];

        const dateS = new Set<string>();
        params?.commentLs.forEach((x: any) => {
            try {
                if (dateS.has(x.date)) return;
                x.ownerId = params.ownerId;
                const c = new Comment(this.coords, x);
                if (x.ownerId === this.coords.user$.currentId) this.addComment(c);
                else otherLs.push(c);
                dateS.add(x.date);
            } catch (e) {
                this.coords.utils$.consoleError('COMMENT LOAD3 ERROR', e);
            }
        });
        return {
            otherLs: otherLs,
        };
    }

    addProjectMemberComments(cLs: Comment[]) {
        cLs.forEach((c) => {
            this.addComment(c);
        });
    }

    get currentUserCommentLs() {
        const ls: Comment[] = [];
        this._comMap.forEach((mp) => mp.forEach((c) => c.isCurrentUser && c.text && ls.push(c)));
        return ls;
    }

    userCommentLs(testId: string) {
        const ls: Comment[] = [];
        this._comMap.forEach((mp) =>
            mp.forEach(
                (c) => c.text && (c.ownerId === testId || c.autouserId === testId) && ls.push(c),
            ),
        );
        return ls;
    }

    addComment(c: Comment) {
        // Safety check: prevent duplicate comments with same text/originId/ownerId but different IDs
        // This is a safety net in case something bypasses the import deduplication
        // NOTE: Only check for duplicates if the comment has actual text content
        // Empty comments are UI placeholders and should always be allowed
        const existingMap = this._comMap.get(c.originId);
        if (existingMap && c.text) {
            const isDuplicate = Array.from(existingMap.values()).some(
                (existingC) =>
                    existingC.text === c.text &&
                    existingC.originId === c.originId &&
                    existingC.ownerId === c.ownerId &&
                    existingC.id !== c.id,
            );
            if (isDuplicate) {
                return;
            }
        }

        let mp = this._comMap.get(c.originId);
        if (!mp) {
            mp = new Map<string, Comment>();
            this._comMap.set(c.originId, mp);
        }
        mp.set(c.id, c);
    }

    getItemMap(id: string) {
        return this._comMap.get(id);
    }

    ofOriginLs(originId: string) {
        const origMp = this._comMap.get(originId);
        if (!origMp) return [this.create(originId)];
        const ls = Array.from(origMp.values()).filter((c) => c.text);

        // Create a set of existing comment signatures to detect duplicates
        // This prevents showing the same comment twice when it exists in both
        // _comMap (project member comments) and autouser comments after import
        const existingCommentSignatures = new Set(
            ls.map((c) => `${c.text}|${c.originId}|${c.ownerId}`),
        );

        // Only add autouser comments that don't duplicate existing comments
        this.coords.autouser$.ofOriginCommentLs(originId).forEach((c) => {
            const signature = `${c.text}|${c.originId}|${c.ownerId}`;
            if (!existingCommentSignatures.has(signature)) {
                ls.push(c);
                existingCommentSignatures.add(signature);
            }
        });

        ls.sort((a, b) => b.date.epochSeconds - a.date.epochSeconds);
        ls.unshift(this.create(originId));
        return ls;
    }

    create(originId: string) {
        const c = new Comment(this.coords, {
            originId: originId,
            ownerId: this.coords.user$.currentId,
        });
        this.addComment(c);
        return c;
    }

    delete(c: Comment) {
        const mp = this._comMap.get(c.originId);
        if (!mp) return;
        mp.delete(c.id);
        if (!mp.size) this._comMap.delete(c.originId);
        this.markForManifestStore();
    }
}
